<script setup>
import { ref, computed } from 'vue';
import api from '@shop/frontend/web/js/api';
import { translator } from '@shop/frontend/web/js/services/TranslatorService';
import Popper from 'vue3-popper';
import { useCartDataProvider } from '@vueApp/hooks/useCartDataProvider.js';
import useCartStore from '@vueApp/store/modules/cart.js';
import IPushkinWarningModal from '@components/IPushkinWarningModal/IPushkinWarningModal.vue';

const cartStore = useCartStore();
const { isGuest, isFastBuy } = useCartDataProvider();

const props = defineProps({
  url: {
    type: String,
    default() {
      return '';
    },
  },
  options: {
    type: Object,
    default() {
      return {};
    },
  },
  smallBtn: {
    type: Boolean,
    default: false,
  },
  ticketsCount: {
    type: Number,
    default: 0,
  },
  firstEventId: {
    type: Number,
    default: 0,
  },
  eventType: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['setAcquiringError']);

const pushkinWarningModalShow = ref(false);
const isAcquiringPushkinEnable = props.options.payType === 'Pushkin';
const isPushkinRulesAccepted = ref(false);

const isPushkinPurchaseEnable = computed(() => {
  return props.ticketsCount === 1 && props.options.payType === 'Pushkin'
});

const isAcquiringPushkinOrCinema = ref(
  props.options.payType === 'Pushkin' || props.options.payType === 'Cinema'
);

const formatEventType = computed(() =>
  props.eventType === 'service_simple' ? 'service' : 'event'
);

const acquiringButtonClass = computed(() => {
  return {
    'js-get-fast-registration': isGuest,
    'i-acquirings-button_small': props.smallBtn,
  };
});

function changePayAcquiring(acquiringId) {
  api.pay
    .changePayAcquiring({ orderId: props.options.orderId, acquiringId })
    .then(res => {
      if (isGuest) {
        cartStore.setCurrentOpenedEventId(props.firstEventId);
        cartStore.setFastBuyModalStatus(true);
        cartStore.setEventTypeToPay(formatEventType.value);
        return;
      }

      if ((props.options.payType === 'Pushkin' || acquiringId === 1) && !isPushkinRulesAccepted.value) {
        pushkinWarningModalShow.value = true;
        return;
      }

      return (window.location.href = props.url);
    })
    .catch((err) => {
      emit('setAcquiringError', {
        isAcquiringError: true,
        errorMessage: err.message ?? '',
      });
    });
}

function payForOrder(event) {
  event?.preventDefault();
  const acquiringId = props.options.id;
  changePayAcquiring(acquiringId);

  if (isGuest) {
    cartStore.setCurrentOpenedEventId(props.firstEventId);
    cartStore.setFastBuyModalStatus(true);
    cartStore.setEventTypeToPay(formatEventType.value);
  }
}

function confirmPushkinRulesAndPay() {
  isPushkinRulesAccepted.value = true;
  payForOrder();
}
</script>

<template>
  <button
    v-if="isGuest && !isFastBuy"
    class="btn i-acquirings-button"
    data-bs-toggle="modal"
    data-bs-target="#loginModal"
  >
    <slot>Авторизоваться для оплаты</slot>
  </button>
  <div v-else class="i-acquirings-button__link-wrapper">
    <a
      :href="url"
      class="button button__primary i-acquirings-button"
      :class="acquiringButtonClass"
      @click="payForOrder($event)"
    >
      <slot>
        <svg class="icon i-acquirings-button__icon">
          <use :xlink:href="`${isAcquiringPushkinOrCinema ? '#pushkin' : '#card'}`" />
        </svg>
        <template v-if="!options.name">
          {{ translator.t('main', 'Pay') }} {{ options.sum }} ₽
        </template>
        <template v-else> {{ options.name }}</template>
      </slot>
    </a>
    <Popper
      v-if="isAcquiringPushkinOrCinema"
      class="light i-acquirings-button__popper"
      placement="top"
      arrow
      hover
      content="С помощью пушкинской карты можно приобрести только 1 место на 1 мероприятие."
    >
      <svg class="i-acquirings-button__pushkin-tooltip" width="12" height="12" fill="none">
        <use xlink:href="#attention" />
      </svg>
    </Popper>
  </div>
  <IPushkinWarningModal
    v-if="isAcquiringPushkinEnable"
    v-model="pushkinWarningModalShow"
    :is-pushkin-purchase-enable="isPushkinPurchaseEnable"
    @continiue-buy="confirmPushkinRulesAndPay"
    @close-pushkin-warning-modal="pushkinWarningModalShow = false"
  />
</template>

<style scoped lang="scss">
@import "frontend/web/src/styles/utils/variables.scss";
@import "./i-acquirings-button.scss";

:deep(.popper) {
  width: 280px;
  padding: 5px;
  border-radius: 6px;
  box-shadow: 0px 5px 5px rgba(0, 0, 0, 0.05);
  background-color: $white;
  color: $black;
  text-align: center;
  font-size: 14px;
  font-weight: 400;
}

:deep(.popper #arrow::before) {
  transform: translateX(-5px) rotate(45deg);
  background: $white;
}
</style>
